<script setup lang="ts">
import moment from 'moment'
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import { useDataBloqueadaStore } from './store/useDataBloqueadaStore'
import type { IDataBloqueada } from './types'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import LayoutTable from '@/components/CDF/LayoutTable.vue'

withDefaults(defineProps<{
  isHeader: boolean
}>(), {
  isHeader: true,
})

definePage({
  meta: {
    action: 'list',
    subject: 'data-bloqueada',
  },
})

const store = useDataBloqueadaStore()

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
  espacoeventos,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  dialogDestroy,
  fetchEspacoEvento,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: 'Data inicial',
      key: 'data_inicial',
      value: item => moment(item).format('DD/MM/YYYY'),
    },

    {
      title: 'Data final',
      key: 'data_final',
      value: item => moment(item).format('DD/MM/YYYY'),
    },

    {
      title: 'Espaço',
      key: 'espaco_evento.nome',
    },
  ] as IHeader[]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: 'Data inicial',
      value: 'data_inicial',
    },

    {
      title: 'Data final',
      value: 'data_final',
    },

    {
      title: 'Espaço',
      value: 'id',
      relationship: 'espacoEvento',
      options: espacoeventos.value,
      type: 'SELECT',
      itemValue: 'id',
      itemTitle: 'nome',
    },
  ] as ITerm[]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'data-bloqueada',
    },
    to: (item: IDataBloqueada) => `/admin/data-bloqueada/editar/${item.id}`,
  },
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'data-bloqueada',
    },
    onClick: (item: IDataBloqueada) => dialogDestroy(item.id),
  },
]

onMounted(() => {
  fetchEspacoEvento()
})

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Data bloqueadas"

    new-item="/admin/data-bloqueada/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :page="page"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #item.data_inicial="{ item }">
      {{ moment(item.data_inicial).format('DD/MM/YYYY') }}
    </template>
    <template #item.data_final="{ item }">
      {{ moment(item.data_final).format('DD/MM/YYYY') }}
    </template>
  </LayoutTable>
</template>
